import { MutableRefObject, RefObject, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { DraftConfiguration } from '@/types';
import { getRotatedRect } from '@/utils/math/getRotatedRect';
import { getBoundingBoxesForConfigurations } from '@/model/configuration/getBoundingBoxesForConfigurations';
import {
  TransformChangedCallback,
  TransformChangedCallbackPayload,
  isLastPayLoad,
} from './TransformChangedContext';
import { getBoundingBoxForRects } from './utils/getBoundingBoxForRects';
import './ConfigurationsFrame.scss';

const CLASS_NAME_TRANSFORMING = 'configurations-frame--transforming';
const CLASS_NAME_INVALID = 'configurations-frame--invalid';

const exceedsSize = (
  width: number,
  height: number,
  maxWidth: number | undefined,
  maxHeight: number | undefined
) => (maxWidth && width > maxWidth) || (maxHeight && height > maxHeight);

const setValue = (length: SVGAnimatedLength, value: number) => (length.baseVal.value = value);

interface ConfigurationsFrameOptions {
  className?: string;
  maxWidth?: number;
  maxHeight?: number;
  fill?: string;
  ref?: RefObject<SVGRectElement>;
}

export const useConfigurationsFrame = (
  configurations: DraftConfiguration[],
  transformCallbacks: MutableRefObject<Set<TransformChangedCallback>>,
  { className, maxWidth, maxHeight, fill, ref }: ConfigurationsFrameOptions = {}
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const svgRect = ref || useRef<SVGRectElement>(null);
  let rects = useMemo(() => getBoundingBoxesForConfigurations(configurations), [configurations]);
  const { x, y, width, height } = useMemo(
    () => getBoundingBoxForRects(Object.values(rects)),
    [rects]
  );

  useEffect(() => {
    const handleTransformChanged = (payload: TransformChangedCallbackPayload) => {
      const svgRectElement = svgRect.current;
      if (!svgRectElement) {
        return;
      }

      if (isLastPayLoad(payload)) {
        svgRectElement.classList.remove(CLASS_NAME_TRANSFORMING);
        return;
      }

      const {
        cid,
        offset: { x, y },
        size: { width, height },
        rotation,
      } = payload;

      // this version of rects only needs to update inside the callback function
      // eslint-disable-next-line react-hooks/exhaustive-deps
      rects = {
        ...rects,
        [cid]: getRotatedRect(rotation, { x, y, width, height }),
      };

      const updatedFrame = getBoundingBoxForRects(Object.values(rects));
      if (!Number.isFinite(updatedFrame.x)) {
        return;
      }

      svgRectElement.classList.add(CLASS_NAME_TRANSFORMING);

      const {
        x: updatedFrameX,
        y: updatedFrameY,
        width: updatedFrameWidth,
        height: updatedFrameHeight,
      } = updatedFrame;
      setValue(svgRectElement.x, updatedFrameX);
      setValue(svgRectElement.y, updatedFrameY);
      setValue(svgRectElement.width, updatedFrameWidth);
      setValue(svgRectElement.height, updatedFrameHeight);

      if (maxWidth || maxHeight) {
        if (exceedsSize(updatedFrameWidth, updatedFrameHeight, maxWidth, maxHeight)) {
          svgRectElement.classList.add(CLASS_NAME_INVALID);
        } else {
          svgRectElement.classList.remove(CLASS_NAME_INVALID);
        }
      }
    };
    const transformCallback = transformCallbacks.current;
    transformCallback.add(handleTransformChanged);
    return () => {
      transformCallback.delete(handleTransformChanged);
    };
  }, [rects, maxWidth, maxHeight]);

  return x === Number.POSITIVE_INFINITY ? (
    <rect ref={svgRect} />
  ) : (
    <rect
      ref={svgRect}
      className={cn('configurations-frame', className, {
        'configurations-frame--invalid': exceedsSize(width, height, maxWidth, maxHeight),
      })}
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
    />
  );
};
